
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface RouteGuardProps {
  children: React.ReactNode;
}

/** Simple full‑screen spinner used while auth state is bootstrapping */
const ScreenSpinner: React.FC = () => (
  <div className="flex h-screen w-screen items-center justify-center">
    <span className="animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 h-12 w-12" />
  </div>
);

/**
 * Protects private routes.
 * – Shows a spinner while auth status is loading
 * – Redirects unauthenticated users to /login
 *   and preserves the intended location in state
 */
export const ProtectedRoute: React.FC<RouteGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <ScreenSpinner />;

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};

/**
 * Prevents authenticated users from accessing public-only routes
 * (e.g. /login or /register). Shows a spinner during auth bootstrap.
 */
export const PublicRoute: React.FC<RouteGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <ScreenSpinner />;

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};
